import React, { useMemo } from 'react';
import { SolutionOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import { GAEventIdentifier, GAEventName } from '../../../section/Common/constants';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import {
  ALL_EXCLUDED, ALL_EXCLUDED_AO3, ALL_EXCLUDED_RR, PODIUM_TITLES, PODIUM_TITLES_AO3, PODIUM_TITLES_RR,
  SEND_TO_CRM, SENT_TO_CRM_AO3, SENT_TO_CRM_RR, SOURCE
} from '../../../core/constants';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

interface IProps {
  book: IBook;
  id: string;
  label?: string;
}

const getPodiumData = (source: string, book: IBook) => {
  const podiumData = {
    [SOURCE.bookstat]: {
      podiumTitleObject: { amazonasin: book.ASIN },
      podiumTitleType: PODIUM_TITLES,
      excludeType: ALL_EXCLUDED
    },
    [SOURCE['royal-road']]: {
      podiumTitleObject: { fiction_id: book['Fiction Id'] },
      podiumTitleType: PODIUM_TITLES_RR,
      excludeType: ALL_EXCLUDED_RR
    },
    [SOURCE.ao3]: {
      podiumTitleObject: { work_id: book.work_id },
      podiumTitleType: PODIUM_TITLES_AO3,
      excludeType: ALL_EXCLUDED_AO3
    }
  };

  return podiumData[source];
};

const ToPodiumTitle: React.FC<IProps> = ({ book, id, label = 'Title' }) => {
  const { source, setActionLoading } = useActions();
  const queryClient = useQueryClient();
  const history = useHistory();
  const podiumData = useMemo(() => getPodiumData(source?.['source'], book), [source, book]);

  const { mutateAsync: mutatePodium } = useUpdateTitle(podiumData?.podiumTitleType);
  const { mutateAsync: mutateExclude } = useUpdateTitle(podiumData?.excludeType);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    globalThis.gtag('event', GAEventName.click, {
      identifier: GAEventIdentifier.titletoPodiumTitles,
      value: book.ASIN,
      book_name: book.Title
    });

    if (!podiumData) {
      showActionNotification({
        type: 'error',
        message: 'Unable to determine the target for this action.'
      });
      return;
    }

    setActionLoading(true);

    const capturedSource = source?.['source'];

    const promise = mutatePodium({ params: podiumData.podiumTitleObject, body: {} });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: 'The Title was successfully sent to the Podium Titles tab.',
          destination: {
            name: 'Podium Titles',
            onGoTo: () => history.push(`${capturedSource}/podium-titles`)
          }
        });
      } else {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Podium Titles tab.'
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: 'The Title has not been sent to the Podium Titles tab.'
      });
    });

    promise.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR || id === SENT_TO_CRM_AO3) {
        showActionNotification({ type: 'info', message: 'Please delete the data from Hubspot.' });
      }
    });

    if (id === ALL_EXCLUDED || id === ALL_EXCLUDED_RR || id === ALL_EXCLUDED_AO3) {
      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.finally(() => setActionLoading(false));
      return;
    }

    mutateExclude({ params: podiumData.podiumTitleObject })
      .then(excludeRes => {
        if (excludeRes?.status === 200) {
          showActionNotification({
            type: 'success',
            message: 'The Title was successfully sent to the Excluded tab.'
          });
        }
        queryClient.refetchQueries(`get-row-count${id}`);
        queryClient.refetchQueries(`get-${id}`);
      })
      .catch(() => {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Podium Titles tab.'
        });
        setActionLoading(false);
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  return (
    <Popover
      placement='topLeft'
      content={<div className='popover-content'>{`Send ${label} to Podium Titles`}</div>}
      trigger='hover'
    >
      <SolutionOutlined
        data-testid='podium-list-action'
        onClick={handleClick}
        className='actions-icons actions-icons-podium-title'
      />
    </Popover>
  );
};

export default React.memo(ToPodiumTitle);
